import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  FileText,
  Plus,
  Clock,
  Users,
  User,
  Sparkles,
  Save,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Wand2,
  Target,
  ListChecks,
  Book,
  CheckCircle2,
  Settings,
} from "lucide-react";
import { DynamicSubjectSelector } from "./DynamicSubjectSelector";
import { NIVEAUX } from "./constants";
import {
  createLessonPlan,
  updateLessonPlan,
  getLessonPlan,
  getLessonPlanTemplates,
  generateLessonPlanWithAI,
} from "../../lib/api";
import { toast } from "sonner";

const GROUPING_OPTIONS = [
  { value: "whole_class", label: "Classe entière", icon: Users },
  { value: "small_groups", label: "Petits groupes", icon: Users },
  { value: "pairs", label: "Binômes", icon: Users },
  { value: "individual", label: "Individuel", icon: User },
];

const DEFAULT_PHASE = {
  title: "",
  duration_minutes: 10,
  description: "",
  teacher_actions: "",
  student_actions: "",
  materials: [],
  grouping: "whole_class",
  differentiation: "",
};

export function LessonPlanEditor({ planId, onSave, onClose, initialData }) {
  const [plan, setPlan] = useState({
    title: "",
    niveaux: [],
    domain_id: null,
    domain_label: "",
    subdomain_id: null,
    subdomain_label: "",
    duration_minutes: 45,
    sequence_position: "",
    objectives: [],
    skill_ids: [],
    prerequisites: [],
    phases: [],
    materials: [],
    evaluation: {
      success_criteria: [],
      observation_points: [],
      assessment_type: "formative",
    },
    extensions: "",
    homework: "",
    notes: "",
    status: "draft",
    tags: [],
  });
  
  const [loading, setLoading] = useState(!!planId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiPrompt, setAiPrompt] = useState({ objective: "", niveau: "ce1", domain: "" });

  // Load existing plan
  useEffect(() => {
    if (planId) {
      const fetchPlan = async () => {
        try {
          const response = await getLessonPlan(planId);
          setPlan(response.data);
        } catch (err) {
          console.error("Failed to load plan:", err);
          toast.error("Erreur lors du chargement");
        } finally {
          setLoading(false);
        }
      };
      fetchPlan();
    } else if (initialData) {
      setPlan(prev => ({ ...prev, ...initialData }));
    }
  }, [planId, initialData]);

  // Load templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await getLessonPlanTemplates();
        setTemplates(response.data || []);
      } catch (err) {
        console.error("Failed to load templates:", err);
      }
    };
    fetchTemplates();
  }, []);

  const handleSave = async () => {
    if (!plan.title.trim()) {
      toast.error("Le titre est obligatoire");
      return;
    }

    setSaving(true);
    try {
      let response;
      if (planId) {
        response = await updateLessonPlan(planId, plan);
      } else {
        response = await createLessonPlan(plan);
      }
      toast.success("Fiche de prép enregistrée");
      onSave?.(response.data);
    } catch (err) {
      console.error("Failed to save plan:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.objective.trim()) {
      toast.error("Veuillez saisir un objectif");
      return;
    }

    setGenerating(true);
    try {
      const response = await generateLessonPlanWithAI({
        objective: aiPrompt.objective,
        niveau: aiPrompt.niveau,
        domain: aiPrompt.domain || plan.domain_label || "Français",
        duration_minutes: plan.duration_minutes,
      });
      
      // Merge generated content with existing plan
      setPlan(prev => ({
        ...prev,
        ...response.data,
        title: response.data.title || prev.title,
      }));
      
      toast.success("Fiche générée par l'IA");
      setShowAIDialog(false);
    } catch (err) {
      console.error("AI generation failed:", err);
      toast.error("Erreur lors de la génération IA");
    } finally {
      setGenerating(false);
    }
  };

  const handleApplyTemplate = (template) => {
    setPlan(prev => ({
      ...prev,
      phases: template.phases.map((p, i) => ({
        ...DEFAULT_PHASE,
        ...p,
        id: `phase_${Date.now()}_${i}`,
        order: i,
      })),
    }));
    setShowTemplates(false);
    toast.success(`Modèle "${template.title}" appliqué`);
  };

  const addPhase = () => {
    setPlan(prev => ({
      ...prev,
      phases: [
        ...prev.phases,
        {
          ...DEFAULT_PHASE,
          id: `phase_${Date.now()}`,
          order: prev.phases.length,
        },
      ],
    }));
  };

  const updatePhase = (index, updates) => {
    setPlan(prev => ({
      ...prev,
      phases: prev.phases.map((phase, i) => 
        i === index ? { ...phase, ...updates } : phase
      ),
    }));
  };

  const removePhase = (index) => {
    setPlan(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index),
    }));
  };

  const movePhase = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= plan.phases.length) return;
    
    const newPhases = [...plan.phases];
    [newPhases[index], newPhases[newIndex]] = [newPhases[newIndex], newPhases[index]];
    newPhases.forEach((p, i) => p.order = i);
    
    setPlan(prev => ({ ...prev, phases: newPhases }));
  };

  const totalDuration = plan.phases.reduce((sum, p) => sum + (p.duration_minutes || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-indigo-600" />
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {planId ? "Modifier la fiche" : "Nouvelle fiche de prép"}
            </h2>
            <p className="text-sm text-slate-500">
              Durée totale : {totalDuration} min / {plan.duration_minutes} min prévues
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowAIDialog(true)}>
            <Wand2 className="w-4 h-4 mr-2" />
            Générer avec l'IA
          </Button>
          <Button variant="outline" onClick={() => setShowTemplates(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Modèles
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
          <TabsList className="mx-4 mt-4">
            <TabsTrigger value="general" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Général
            </TabsTrigger>
            <TabsTrigger value="phases" className="flex items-center gap-2">
              <ListChecks className="w-4 h-4" />
              Déroulé ({plan.phases.length})
            </TabsTrigger>
            <TabsTrigger value="evaluation" className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Évaluation
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 px-4 pb-4">
            {/* General Tab */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-sm font-medium">Titre de la séance *</label>
                  <Input
                    value={plan.title}
                    onChange={(e) => setPlan(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ex: Découverte de la multiplication"
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Niveau(x)</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {NIVEAUX.map((niveau) => (
                      <Badge
                        key={niveau.value}
                        variant={plan.niveaux.includes(niveau.value) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => {
                          setPlan(prev => ({
                            ...prev,
                            niveaux: prev.niveaux.includes(niveau.value)
                              ? prev.niveaux.filter(n => n !== niveau.value)
                              : [...prev.niveaux, niveau.value]
                          }));
                        }}
                      >
                        {niveau.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Durée prévue (minutes)</label>
                  <Input
                    type="number"
                    value={plan.duration_minutes}
                    onChange={(e) => setPlan(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 45 }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Domaine / Matière</label>
                  <DynamicSubjectSelector
                    value={plan.domain_id}
                    onChange={(id, subject) => setPlan(prev => ({
                      ...prev,
                      domain_id: id,
                      domain_label: subject?.label || "",
                    }))}
                    niveau={plan.niveaux[0] || "ce1"}
                    className="w-full mt-1"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Place dans la séquence</label>
                  <Input
                    value={plan.sequence_position}
                    onChange={(e) => setPlan(prev => ({ ...prev, sequence_position: e.target.value }))}
                    placeholder="Ex: Séance 3/8, Introduction, Évaluation"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Objectifs d'apprentissage</label>
                <div className="space-y-2 mt-1">
                  {plan.objectives.map((obj, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={obj}
                        onChange={(e) => {
                          const newObjectives = [...plan.objectives];
                          newObjectives[i] = e.target.value;
                          setPlan(prev => ({ ...prev, objectives: newObjectives }));
                        }}
                        placeholder="Objectif..."
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setPlan(prev => ({
                            ...prev,
                            objectives: prev.objectives.filter((_, j) => j !== i)
                          }));
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPlan(prev => ({ ...prev, objectives: [...prev.objectives, ""] }))}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un objectif
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Prérequis</label>
                <Textarea
                  value={plan.prerequisites.join("\n")}
                  onChange={(e) => setPlan(prev => ({
                    ...prev,
                    prerequisites: e.target.value.split("\n").filter(p => p.trim())
                  }))}
                  placeholder="Un prérequis par ligne..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Matériel nécessaire</label>
                <Textarea
                  value={plan.materials.join("\n")}
                  onChange={(e) => setPlan(prev => ({
                    ...prev,
                    materials: e.target.value.split("\n").filter(m => m.trim())
                  }))}
                  placeholder="Un élément par ligne..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </TabsContent>

            {/* Phases Tab */}
            <TabsContent value="phases" className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant={totalDuration === plan.duration_minutes ? "default" : "destructive"}>
                    {totalDuration} / {plan.duration_minutes} min
                  </Badge>
                  {totalDuration !== plan.duration_minutes && (
                    <span className="text-sm text-orange-600">
                      {totalDuration > plan.duration_minutes ? "Dépasse de " : "Manque "}
                      {Math.abs(totalDuration - plan.duration_minutes)} min
                    </span>
                  )}
                </div>
                <Button onClick={addPhase}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une phase
                </Button>
              </div>

              <Accordion type="multiple" defaultValue={plan.phases.map((_, i) => `phase-${i}`)}>
                {plan.phases.map((phase, index) => (
                  <AccordionItem key={phase.id || index} value={`phase-${index}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 w-full">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => { e.stopPropagation(); movePhase(index, "up"); }}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => { e.stopPropagation(); movePhase(index, "down"); }}
                            disabled={index === plan.phases.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        <Badge variant="outline">{index + 1}</Badge>
                        <span className="font-medium">{phase.title || "Phase sans titre"}</span>
                        <Badge className="ml-auto mr-4">
                          <Clock className="w-3 h-3 mr-1" />
                          {phase.duration_minutes} min
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4 pt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Nom de la phase</label>
                          <Input
                            value={phase.title}
                            onChange={(e) => updatePhase(index, { title: e.target.value })}
                            placeholder="Ex: Mise en situation, Recherche..."
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-sm font-medium">Durée</label>
                            <Input
                              type="number"
                              value={phase.duration_minutes}
                              onChange={(e) => updatePhase(index, { duration_minutes: parseInt(e.target.value) || 0 })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium">Modalité</label>
                            <Select
                              value={phase.grouping}
                              onValueChange={(v) => updatePhase(index, { grouping: v })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {GROUPING_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium">Description / Consigne</label>
                        <Textarea
                          value={phase.description}
                          onChange={(e) => updatePhase(index, { description: e.target.value })}
                          placeholder="Décrivez cette phase..."
                          className="mt-1"
                          rows={2}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Rôle de l'enseignant</label>
                          <Textarea
                            value={phase.teacher_actions}
                            onChange={(e) => updatePhase(index, { teacher_actions: e.target.value })}
                            placeholder="Ce que fait l'enseignant..."
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Activité des élèves</label>
                          <Textarea
                            value={phase.student_actions}
                            onChange={(e) => updatePhase(index, { student_actions: e.target.value })}
                            placeholder="Ce que font les élèves..."
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium">Différenciation</label>
                        <Textarea
                          value={phase.differentiation || ""}
                          onChange={(e) => updatePhase(index, { differentiation: e.target.value })}
                          placeholder="Adaptations pour les élèves en difficulté ou avancés..."
                          className="mt-1"
                          rows={2}
                        />
                      </div>

                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removePhase(index)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer cette phase
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {plan.phases.length === 0 && (
                <Card className="border-dashed">
                  <CardContent className="py-8 text-center">
                    <ListChecks className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">Aucune phase définie</p>
                    <div className="flex justify-center gap-2">
                      <Button onClick={addPhase}>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter une phase
                      </Button>
                      <Button variant="outline" onClick={() => setShowTemplates(true)}>
                        Utiliser un modèle
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Evaluation Tab */}
            <TabsContent value="evaluation" className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium">Critères de réussite</label>
                <Textarea
                  value={plan.evaluation?.success_criteria?.join("\n") || ""}
                  onChange={(e) => setPlan(prev => ({
                    ...prev,
                    evaluation: {
                      ...prev.evaluation,
                      success_criteria: e.target.value.split("\n").filter(c => c.trim())
                    }
                  }))}
                  placeholder="Un critère par ligne...&#10;Ex: L'élève est capable de...&#10;Ex: L'élève identifie correctement..."
                  className="mt-1"
                  rows={4}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Points d'observation</label>
                <Textarea
                  value={plan.evaluation?.observation_points?.join("\n") || ""}
                  onChange={(e) => setPlan(prev => ({
                    ...prev,
                    evaluation: {
                      ...prev.evaluation,
                      observation_points: e.target.value.split("\n").filter(p => p.trim())
                    }
                  }))}
                  placeholder="Points à observer pendant la séance..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Prolongements</label>
                <Textarea
                  value={plan.extensions || ""}
                  onChange={(e) => setPlan(prev => ({ ...prev, extensions: e.target.value }))}
                  placeholder="Activités de prolongement, liens avec d'autres séances..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Devoirs</label>
                <Textarea
                  value={plan.homework || ""}
                  onChange={(e) => setPlan(prev => ({ ...prev, homework: e.target.value }))}
                  placeholder="Travail à la maison..."
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Notes personnelles</label>
                <Textarea
                  value={plan.notes || ""}
                  onChange={(e) => setPlan(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Remarques, ajustements après la séance..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </div>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modèles de séance</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {templates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-indigo-300 transition-colors"
                onClick={() => handleApplyTemplate(template)}
              >
                <CardContent className="p-4">
                  <h4 className="font-medium">{template.title}</h4>
                  <p className="text-sm text-slate-500">{template.description}</p>
                  <div className="flex gap-1 mt-2">
                    {template.phases?.map((phase, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {phase.title} ({phase.duration_minutes}')
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-indigo-600" />
              Générer avec l'IA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Objectif principal *</label>
              <Textarea
                value={aiPrompt.objective}
                onChange={(e) => setAiPrompt(prev => ({ ...prev, objective: e.target.value }))}
                placeholder="Ex: Découvrir la notion de multiplication comme addition répétée"
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Niveau</label>
                <Select
                  value={aiPrompt.niveau}
                  onValueChange={(v) => setAiPrompt(prev => ({ ...prev, niveau: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NIVEAUX.map((n) => (
                      <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Domaine</label>
                <Input
                  value={aiPrompt.domain || plan.domain_label}
                  onChange={(e) => setAiPrompt(prev => ({ ...prev, domain: e.target.value }))}
                  placeholder="Ex: Mathématiques"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAIDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleGenerateAI} disabled={generating || !aiPrompt.objective.trim()}>
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LessonPlanEditor;
